// CTSK-525 — shared card-view state for pages other than the Documents list
// (Watching). Same prefs store as the Documents page, so a mode picked on one
// page is the default on the other.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import {
  effectiveViewMode, loadCols, loadHighlightEffect, loadPreviewAll, loadScaled, loadViewMode, saveCols,
  savePreviewAll, saveScaled, saveViewMode, takeLastOpened, type ColsPref, type HighlightEffect, type ViewMode,
} from './viewPrefs.js';

export interface CardViewState {
  wide: boolean;
  savedMode: ViewMode;
  mode: ViewMode;
  setMode: (m: ViewMode) => void;
  cols: ColsPref;
  setCols: (c: ColsPref) => void;
  previewAll: boolean;
  setPreviewAll: (v: boolean) => void;
  scaled: boolean;
  setScaled: (v: boolean) => void;
  expanded: ReadonlySet<string>;
  toggleCard: (id: string) => void;
  highlightId: string | null;
  highlightEffect: HighlightEffect;
}

export function useCardView(userId: string | null, orgId: string | null): CardViewState {
  const wide = useMediaQuery('(min-width: 900px)', true);
  const [savedMode, setSavedMode] = useState<ViewMode>(() => loadViewMode(userId, orgId));
  const [cols, setColsState] = useState<ColsPref>(() => loadCols(userId, orgId));
  const [previewAll, setPreviewAllState] = useState(() => loadPreviewAll(userId, orgId));
  const [scaled, setScaledState] = useState(() => loadScaled(userId, orgId));
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [highlightId, setHighlightId] = useState<string | null>(() => takeLastOpened());
  const highlightEffect = useMemo(() => loadHighlightEffect(), []);
  useEffect(() => {
    setSavedMode(loadViewMode(userId, orgId));
    setColsState(loadCols(userId, orgId));
    setPreviewAllState(loadPreviewAll(userId, orgId));
    setScaledState(loadScaled(userId, orgId));
  }, [userId, orgId]);
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 8000);
    return () => clearTimeout(t);
  }, [highlightId]);
  const toggleCard = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  return {
    wide, savedMode, mode: effectiveViewMode(savedMode, wide),
    setMode: (m) => { setSavedMode(m); saveViewMode(m, userId, orgId); },
    cols, setCols: (c) => { setColsState(c); saveCols(c, userId, orgId); },
    previewAll, setPreviewAll: (v) => { setPreviewAllState(v); savePreviewAll(v, userId, orgId); },
    scaled, setScaled: (v) => { setScaledState(v); saveScaled(v, userId, orgId); },
    expanded, toggleCard, highlightId, highlightEffect,
  };
}
